import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ContentTypesEnum } from '@enums/content-type.enum';
import { DateFormatEnum } from '@enums/date-format.enum';
import { ExtraProductsEnum } from '@enums/extra-products.enum';
import { ExtrasTypesEnum } from '@enums/extras-types';
import { PaxTypeEnum } from '@enums/pax-type.enum';
import { AirportAssistanceContent, BaggageContent } from '@shared/services/content/_interfaces_/content.interface';
import { AirportAssistanceContentMapped, BaggageContentMapped } from '@shared/services/content/_interfaces_/content.mapped.interface';
import { ContentMapperService } from '@shared/services/content/content-mapper.service';
import { ContentService } from '@shared/services/content/content.service';
import { ExtrasInfo } from '@shared/services/extras/_interfaces_/extras-info.interface';
import { ExtrasOverviewService } from '@shared/services/extras/extras-overview.service';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, finalize, map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-airport-assistance-accordion',
  templateUrl: './airport-assistance-accordion.component.html',
  styleUrls: ['./airport-assistance-accordion.component.scss'],
})
export class AirportAssistanceAccordionComponent implements OnInit {
  private contentService = inject(ContentService);
  private contentMapperService = inject(ContentMapperService);
  private cd = inject(ChangeDetectorRef);
  private extrasOverviewService = inject(ExtrasOverviewService);

  @Input() extrasInfo: ExtrasInfo;
  @Input() culture: string;
  @Input() expandExtras: string;
  @Input() extrasTypesEnum = ExtrasTypesEnum;
  @Output() assistanceSelectionChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  private unsubscribeAll$: Subject<void> = new Subject();
  public flightIdentifier: string = '';
  public airportAssistanceContent: AirportAssistanceContentMapped;
  public ddMMyyyy: string = DateFormatEnum.ddMMyyyy;
  public showSummarize: boolean = false;
  public loading: boolean = false;
  public loadingIndex: number | null = null;
  public loadingProductIndex: number | null = null;
  public passengerType = PaxTypeEnum;
  public isAirportAssistanceOption: boolean;

  ngOnInit(): void {
    this.flightIdentifier = this.extrasInfo?.flightIdentifier;
    this.isAirportAssistanceOption =
      this.extrasInfo?.passengers[0]?.passengerExtras.filter((service) => service.type === ExtrasTypesEnum.MedicalAssistance)[0].options
        .length > 0;
    this.airportAssistance();
    this.summarizeAirportAssitanceSelction();
  }

  readonly contentairportAssistance$: Observable<BaggageContentMapped> = this.contentService
    .getContent<BaggageContent>(ContentTypesEnum.myFfExtrasPage)
    .pipe(map((content: BaggageContent) => this.contentMapperService.mapBaggageContent(content)));

  public airportAssistance(): void {
    this.contentService
      .getContent<AirportAssistanceContent>(ContentTypesEnum.myFfExtrasPage)
      .pipe(map((content: AirportAssistanceContent) => this.contentMapperService.mapAirportAssistanceContent(content)))
      .subscribe((data) => (this.airportAssistanceContent = data));
  }

  parseServiceCode(serviceCode: string): string {
    if (this.airportAssistanceContent) {
      return this.airportAssistanceContent[serviceCode];
    }
  }

  selectAirportAssistance(assistanceOption: any, passengerIdentifier: string, passengerIndex: number, productIndex: number): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    this.loadingIndex = passengerIndex;
    this.loadingProductIndex = productIndex;

    this.extrasOverviewService
      .putAirportAssistance(this.flightIdentifier, passengerIdentifier, assistanceOption.bookingReference, assistanceOption.isSelected)
      .pipe(
        take(1),
        takeUntil(this.unsubscribeAll$),
        catchError(() => {
          return EMPTY;
        }),
        finalize(() => {
          this.loading = false;
          this.loadingIndex = null;
          this.cd.markForCheck();
        })
      )
      .subscribe((data) => {
        this.extrasInfo = data;
        this.extrasOverviewService.setExtrasData(data);
        this.summarizeAirportAssitanceSelction();
        this.isAnyAirportAssistanceSelected();
        this.cd.markForCheck();
      });
  }

  public summarizeAirportAssitanceSelction(): string {
    const airportAssistancePerPassenger = this.extrasInfo.passengers.map(
      (passenger) => passenger.passengerExtras[ExtraProductsEnum.airportAssistance]
    );

    const airportAssistanceOptions = airportAssistancePerPassenger
      .map((passenger) => passenger.options?.filter((option) => option?.isSelected))
      .filter((option) => option.length > 0);
    const sumOfSelection = airportAssistanceOptions?.reduce((acc, c) => (acc += c?.length), 0);

    this.showSummarize = sumOfSelection > 0;

    if (this.airportAssistanceContent?.selectionSummary) {
      return this.contentService.parseContentfulDynamicContent(
        this.airportAssistanceContent?.selectionSummary,
        sumOfSelection,
        airportAssistanceOptions.length
      );
    }
  }

  public isAnyAirportAssistanceSelected(): boolean {
    this.assistanceSelectionChanged.emit(this.showSummarize);
    return this.showSummarize;
  }
}
